import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Slider } from '@/components/ui/slider'
import {
  type VideoMetadata,
  VideoPlayerTarget,
} from '@/components/video-player'
import { ClipTagBar } from '@/components/video-player/ClipTagBar'
import { Toggle } from '@/components/ui/toggle'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn, formatVideoDuration } from '@/lib/utils'
import { useDioxusWithProps } from '@/lib/wasm'
import { useDocumentStore } from '@/stores/documentStore'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import { documentClipsOptions } from '@/data/queryOptions'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useParams } from '@tanstack/react-router'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Loader2,
  MaximizeIcon,
  PauseIcon,
  PlayIcon,
  Volume2Icon,
  VolumeOffIcon,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from 'react'
import { usePrefetchStepsThumbnails } from '@/hooks/thumbnails/usePrefetchStepsThumbnails'
import { StepsThumbnailProbes } from '@/hooks/thumbnails/useStepsThumbnails'
import { sortedIndex } from 'lodash'

export const Route = createFileRoute(
  '/_authenticated/org/$orgId/projects/$projectId/sops/$sopId/full-video',
)({
  component: memo(RouteComponent),
})

function RouteComponent() {
  const documentData = useDocumentStore((state) => state.documentData)
  const primaryVideoDocument = useMemo(() => {
    return documentData.documentRefs?.filter((ref) =>
      ref.mimeType?.startsWith('video/'),
    )[0]
  }, [documentData])

  const startTimes = useMemo(() => {
    return (
      documentData.content?.steps
        ?.filter((step) => step.type === 'video')
        .map((step) => step.start) ?? []
    )
  }, [documentData.content?.steps])

  const stepRefs = useRef<HTMLDivElement[]>(Array(startTimes.length).fill(null))
  const videoRef = useRef<HTMLVideoElement>(null)

  const [duration, setDuration] = useState(0)

  const [playing, setPlaying] = useState(false)

  usePrefetchStepsThumbnails()
  const [stepThumbnails, setStepThumbnails] = useState<
    Record<string, string>
  >({})
  const handleStepThumbnailResolved = useCallback(
    (stepId: string, url: string | undefined) => {
      setStepThumbnails((prev) => {
        const current = prev[stepId]
        if (url === current) return prev
        const next = { ...prev }
        if (url) next[stepId] = url
        else delete next[stepId]
        return next
      })
    },
    [],
  )
  const [currentStep, setCurrentStep] = useState<number>(0)
  const [fullScreen, setFullScreen] = useState(false)

  const handlePlayPause = useCallback(() => {
    setPlaying(!playing)
    if (videoRef.current) {
      if (playing) {
        videoRef.current.play()
      }
    }
  }, [playing, videoRef])

  const [currentTime, setCurrentTime] = useState(0)
  const handleSeekChange = useCallback(
    (value: number[]) => {
      console.log('seek change', value)
      setCurrentTime(value[0])
      if (videoRef.current) {
        videoRef.current.currentTime = value[0]
      }
    },
    [videoRef],
  )

  useEffect(() => {
    const video = videoRef.current
    return () => {
      if (video) {
        video.playbackRate = 1
      }
    }
  }, [])

  const [speed, setSpeed] = useState(1)
  const handleSpeedChange = useCallback(
    (item: number) => {
      setSpeed(item)
      if (videoRef.current) {
        videoRef.current.playbackRate = item
      }
    },
    [videoRef],
  )

  const handleTimeUpdate = useCallback(
    (currentTime: number) => {
      setCurrentTime(currentTime)
      const index = sortedIndex(startTimes, currentTime)
      if (index >= 0 && index - 1 !== currentStep) {
        setCurrentStep(index - 1)
      }
    },
    [currentStep, startTimes],
  )

  useEffect(() => {
    if (stepRefs.current[currentStep]) {
      stepRefs.current[currentStep].scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
      })
    }
  }, [currentStep])

  const handleEnded = useCallback(() => {
    setPlaying(false)
  }, [setPlaying])

  const handleLoadedMetadata = useCallback(
    (metadata: VideoMetadata) => {
      setDuration(metadata.duration)
    },
    [setDuration],
  )

  const handleStepClick = useCallback(
    (startTime: number) => {
      if (videoRef.current) {
        videoRef.current.currentTime = startTime + 0.01
      }
    },
    [videoRef],
  )

  const [buffering, setBuffering] = useState(false)
  const [muted, setMuted] = useState(false)
  const [isHovering, setIsHovering] = useState(false)

  // Clip analysis panel — collapsed by default, lazy-fetched on expand
  const [analysisExpanded, setAnalysisExpanded] = useState(false)
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const { projectId } = useParams({
    from: '/_authenticated/org/$orgId/projects/$projectId/sops/$sopId/full-video',
  })
  const { data: clipsData, isLoading: clipsLoading } = useQuery(
    documentClipsOptions({
      tenantId: TENANT_ID!,
      token: idToken!,
      projectId,
      documentId: primaryVideoDocument?.id ?? '',
      enabled:
        analysisExpanded &&
        !!primaryVideoDocument?.id &&
        !!idToken &&
        !!TENANT_ID,
    }),
  )

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted
    }
  }, [muted])

  const handleSeeked = useCallback(() => {
    setBuffering(false)
  }, [setBuffering])
  const handleSeeking = useCallback(() => {
    setBuffering(true)
  }, [setBuffering])

  const handleFullScreen = useCallback(() => {
    setFullScreen(!fullScreen)
  }, [fullScreen])

  useLayoutEffect(() => {
    function handleFullscreenChange() {
      setFullScreen(false)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  })

  if (!primaryVideoDocument) {
    return <div>No video found</div>
  }

  return (
    <div className="flex flex-col gap-4">
      <div
        className="group relative cursor-pointer overflow-hidden rounded-md bg-black"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={handlePlayPause}
      >
        <VideoPlayerTarget
          placeholder={<div>Loading...</div>}
          videoSrcId={primaryVideoDocument.id}
          startTime={0}
          endTime={duration}
          playing={playing}
          owner={true}
          style={{
            maxHeight: '488px',
            margin: 'auto',
            pointerEvents: 'none',
          }}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          onLoadedMetadata={handleLoadedMetadata}
          onSeeked={handleSeeked}
          onSeeking={handleSeeking}
          ref={videoRef}
          fullScreen={fullScreen}
        />

        {/* Buffering overlay */}
        {buffering && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50">
            <Loader2 className="h-16 w-16 animate-spin text-white" />
          </div>
        )}

        {/* Center play/pause button on hover */}
        <div
          className={cn(
            'pointer-events-none absolute inset-0 z-20 flex items-center justify-center transition-opacity duration-200',
            isHovering ? 'opacity-100' : 'opacity-0',
          )}
        >
          <button
            className="pointer-events-auto flex h-32 w-32 items-center justify-center hover:bg-transparent"
            onClick={(e) => {
              e.stopPropagation()
              handlePlayPause()
            }}
          >
            {playing ? (
              <PauseIcon
                className="h-10 w-10 text-white drop-shadow-lg"
                strokeWidth={1}
              />
            ) : (
              <PlayIcon
                className="h-10 w-10 text-white drop-shadow-lg"
                strokeWidth={1}
              />
            )}
          </button>
        </div>

        {/* Bottom right controls */}
        <div
          className={cn(
            'pointer-events-none absolute right-2 bottom-2 z-20 flex gap-2 transition-opacity duration-200',
            isHovering ? 'opacity-100' : 'opacity-0',
          )}
        >
          {/* Speed control */}
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={(e) => e.stopPropagation()}
                    className="pointer-events-auto rounded-sm border-none bg-black/50 text-xs text-white hover:bg-black/70 hover:text-white"
                  >
                    {speed}x
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>Playback Speed</TooltipContent>
            </Tooltip>
            <DropdownMenuContent>
              {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((item) => (
                <DropdownMenuItem
                  key={item}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSpeedChange(item)
                  }}
                  className={cn(
                    speed === item && 'bg-primary text-primary-foreground',
                  )}
                >
                  <span>{item}x</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mute toggle */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Toggle
                variant="outline"
                pressed={muted}
                onPressedChange={(pressed) => setMuted(pressed)}
                onClick={(e) => e.stopPropagation()}
                className="pointer-events-auto rounded-sm border-none bg-black/50 hover:bg-black/70 data-[state=on]:bg-black/70"
                aria-label={muted ? 'Unmute' : 'Mute'}
              >
                {muted ? (
                  <VolumeOffIcon className="h-4 w-4 text-white" />
                ) : (
                  <Volume2Icon className="h-4 w-4 text-white" />
                )}
              </Toggle>
            </TooltipTrigger>
            <TooltipContent>{muted ? 'Unmute' : 'Mute'}</TooltipContent>
          </Tooltip>

          {/* Fullscreen button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation()
                  handleFullScreen()
                }}
                className="pointer-events-auto rounded-sm border-none bg-black/50 hover:bg-black/70"
                aria-label="Fullscreen"
              >
                <MaximizeIcon className="h-4 w-4 text-white" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Fullscreen</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Progress bar below video */}
      <div className="flex flex-row items-center gap-2">
        <span className="text-copy-secondary text-xs">
          {formatVideoDuration(currentTime)}
        </span>
        <Slider
          min={0}
          max={duration}
          step={0.01}
          value={[currentTime]}
          onValueChange={handleSeekChange}
          className="flex-1"
          variant="video"
        />
        <span className="text-copy-secondary text-xs">
          {formatVideoDuration(duration)}
        </span>
      </div>
      {/* Clip analysis panel */}
      <div>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-xs"
          onClick={() => setAnalysisExpanded(!analysisExpanded)}
        >
          {analysisExpanded ? (
            <ChevronUpIcon className="h-3.5 w-3.5" />
          ) : (
            <ChevronDownIcon className="h-3.5 w-3.5" />
          )}
          Analysis
        </Button>
        {analysisExpanded && (
          <div className="mt-1">
            {clipsLoading ? (
              <div className="flex items-center justify-center py-3">
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              </div>
            ) : clipsData && clipsData.clips.length > 0 && duration > 0 ? (
              <ClipTagBar
                clips={clipsData.clips}
                duration={duration}
                currentTime={currentTime}
                onSeek={(v) => handleSeekChange([v])}
              />
            ) : (
              <p className="text-muted-foreground py-2 text-xs">
                No clip analysis available
              </p>
            )}
          </div>
        )}
      </div>

      <StepsThumbnailProbes
        steps={documentData.content?.steps ?? []}
        onResolved={handleStepThumbnailResolved}
      />
      <StepsPreviewWasm
        steps={(documentData.content?.steps ?? []).filter(
          (s) => s.type === 'video',
        )}
        thumbnails={stepThumbnails}
        currentStep={currentStep}
        onStepSeek={handleStepClick}
      />
    </div>
  )
}

/** Thin React wrapper that mounts the Dioxus StepsPreview and bridges the seek callback */
function StepsPreviewWasm({
  steps,
  thumbnails,
  currentStep,
  onStepSeek,
}: {
  steps: import('@/data/entities').DocumentStep[]
  thumbnails: Record<string, string>
  currentStep: number
  onStepSeek: (time: number) => void
}) {
  const ref = useDioxusWithProps('mount_steps_preview', {
    steps: steps.map((s) =>
      s.type === 'video'
        ? {
            id: s.id,
            title: s.title,
            source: s.source,
            keyFrame: s.keyFrame,
            start: s.start,
            end: s.end,
            thumbnail: thumbnails[s.id] ?? null,
          }
        : {
            id: s.id,
            title: s.title,
            source: null,
            keyFrame: 0,
            start: 0,
            end: 0,
            thumbnail: thumbnails[s.id] ?? null,
          },
    ),
    currentStep,
  })

  // Listen for 'step-seek' CustomEvent dispatched by Dioxus
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const handler = (e: Event) => {
      const time = (e as CustomEvent).detail
      if (typeof time === 'number') onStepSeek(time)
    }
    el.addEventListener('step-seek', handler)
    return () => el.removeEventListener('step-seek', handler)
  }, [ref, onStepSeek])

  return <div ref={ref} className="contents" />
}
